import type { ChartLineConfig } from "../insightsMockData";
import type { InsightsChartMetric } from "../types";

type TooltipPayloadItem = {
  dataKey?: string | number;
  value?: number;
};

type PerformanceChartTooltipProps = {
  active?: boolean;
  label?: string;
  payload?: TooltipPayloadItem[];
  lines: ChartLineConfig[];
  chartMetric: InsightsChartMetric;
};

const METRIC_LABELS: Record<InsightsChartMetric, string> = {
  impressions: "Impressions",
  clicks: "Clicks",
  conversions: "Conversions",
  "ad-sales": "Immediate sales",
};

function formatTooltipMonth(label: string): string {
  const match = label.match(/^([A-Za-z]{3,})\s+(\d{4})$/);
  if (!match) {
    return label;
  }
  const [, monthAbbrev, year] = match;
  const date = new Date(`${monthAbbrev} 1, ${year}`);
  if (Number.isNaN(date.getTime())) {
    return label;
  }
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    year: "numeric",
  }).format(date);
}

function formatTooltipValue(
  value: number,
  chartMetric: InsightsChartMetric
): string {
  if (chartMetric === "ad-sales") {
    return `$${Math.round(value).toLocaleString("en-US")}`;
  }
  return Math.round(value).toLocaleString("en-US");
}

export function PerformanceChartTooltip({
  active,
  label,
  payload,
  lines,
  chartMetric,
}: PerformanceChartTooltipProps) {
  if (!active || !payload?.length || !label) {
    return null;
  }

  const lineByKey = new Map(lines.map((line) => [line.key, line]));
  const rows = payload
    .map((entry) => {
      const key = String(entry.dataKey ?? "");
      const line = lineByKey.get(key);
      if (!line || entry.value == null) {
        return null;
      }
      return {
        key,
        label: line.label,
        color: line.color,
        value: entry.value,
      };
    })
    .filter((row): row is NonNullable<typeof row> => row != null);

  if (rows.length === 0) {
    return null;
  }

  return (
    <div className="bg-fs-surface-primary rounded p-4 shadow-[0_2px_6px_rgba(0,0,0,0.15)]">
      <p className="type-fs-label-medium text-(--slate-color-grey-1000)">
        {METRIC_LABELS[chartMetric]}: {formatTooltipMonth(label)}
      </p>
      <div className="mt-2 flex flex-col gap-2">
        {rows.map((row) => (
          <div key={row.key} className="flex items-center gap-3">
            <span
              className="size-2.5 shrink-0 rounded-[2px] opacity-90"
              style={{ backgroundColor: row.color }}
              aria-hidden
            />
            <p className="type-fs-label text-[#333]">
              {row.label}: {formatTooltipValue(row.value, chartMetric)}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
